import { Case, LandTransporter, BufferTimeSettings } from '../types';
import { DriverSchedule } from '../types/schedule';
import { timeToMinutes } from '../utils/timeSlotUtils';
import {
  addBufferToEndTime,
  subtractBufferFromStartTime,
  DEFAULT_BUFFER_TIME_SETTINGS,
} from '../utils/timePickerDefaults';
import * as driverScheduleService from './driverScheduleService';

/**
 * バリデーション結果の型定義
 */
export interface ValidationResult {
  canAssign: boolean;
  reason?: string;
}

/**
 * スケジュール関連のバリデーションを行うサービスクラス
 * FL-55のリファクタリング: ビジネスロジックをUIから分離
 */
/**
 * スケジュール関連のバリデーションを行うサービスクラス
 * FL-55のリファクタリング: ビジネスロジックをUIから分離
 *
 * Phase 5: working_days → driver_schedules テーブルへの移行対応
 * - 各メソッドを async 化し、driverScheduleService 経由でDB照会
 * - selectedDayOfWeek パラメータは段階的に廃止
 */
export class ScheduleValidationService {
  /**
   * 案件の終了時間を計算する
   * @param case_ - 対象案件
   * @param transporterId - 陸送員ID
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns 案件終了時間（HH:mm形式）
   */
  static async getCaseEndTime(
    case_: Case,
    transporterId: string,
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<string> {
    // 当日配送の場合は指定配送時刻を使用
    if (case_.deliveryDate === selectedDate) {
      return case_.deliveryTime;
    }

    // 翌日配送の場合はマイスケジュールの終了時刻を使用
    try {
      const schedule = await this.getScheduleForDate(transporterId, selectedDate);
      if (schedule) {
        return schedule.timeEnd;
      }
    } catch (error) {
      console.error(`Failed to get schedule for case end time: ${error}`);
    }

    // フォールバック: 利用可能な陸送員から勤務終了時刻を取得（後方互換性）
    const transporter = availableTransporters.find(t => t.id === transporterId);
    if (transporter?.workingDays) {
      // availableTransportersから曜日を判定
      const date = new Date(selectedDate);
      const dayOfWeek = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ][date.getDay()] as keyof LandTransporter['workingDays'];
      const workingHours = transporter.workingDays[dayOfWeek];
      if (workingHours) {
        return workingHours.endTime;
      }
    }

    return '18:00'; // デフォルト
  }

  /**
   * FL-81: バッファ時間を考慮した案件の終了時間を計算する
   * @param case_ - 対象案件
   * @param transporterId - 陸送員ID
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @param bufferSettings - バッファ時間設定
   * @returns バッファ時間を加味した案件終了時間（HH:mm形式）
   */
  static async getCaseEndTimeWithBuffer(
    case_: Case,
    transporterId: string,
    selectedDate: string,
    availableTransporters: LandTransporter[],
    bufferSettings: BufferTimeSettings = DEFAULT_BUFFER_TIME_SETTINGS
  ): Promise<string> {
    const baseEndTime = await this.getCaseEndTime(
      case_,
      transporterId,
      selectedDate,
      availableTransporters
    );

    // 案件間移動バッファを追加
    return addBufferToEndTime(baseEndTime, bufferSettings.betweenCasesBuffer);
  }

  /**
   * FL-81: バッファ時間を考慮した案件の開始時間を計算する
   * @param case_ - 対象案件
   * @param bufferSettings - バッファ時間設定
   * @param isFirstCaseOfDay - その日の最初の案件かどうか
   * @returns バッファ時間を加味した案件開始時間（HH:mm形式）
   */
  static getCaseStartTimeWithBuffer(
    case_: Case,
    bufferSettings: BufferTimeSettings = DEFAULT_BUFFER_TIME_SETTINGS,
    isFirstCaseOfDay: boolean = false
  ): string {
    const baseStartTime = case_.pickupTimeStart;

    // その日の最初の案件の場合は準備時間バッファを適用
    if (isFirstCaseOfDay) {
      return subtractBufferFromStartTime(baseStartTime, bufferSettings.dailyPreparationBuffer);
    }

    return baseStartTime;
  }

  /**
   * 時間重複をチェックする
   * @param newCase - 新規割り当て案件
   * @param transporterId - 陸送員ID
   * @param existingCases - 既存の案件リスト
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns 重複がある場合true
   */
  static async checkTimeOverlap(
    newCase: Case,
    transporterId: string,
    existingCases: Case[],
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<boolean> {
    // 該当陸送員の既存案件を抽出
    const transporterCases = existingCases.filter(
      c => c.assignedLandTransporterId === transporterId
    );

    const newCaseStart = timeToMinutes(newCase.pickupTimeStart);
    const newCaseEnd = timeToMinutes(
      await this.getCaseEndTime(newCase, transporterId, selectedDate, availableTransporters)
    );

    for (const existingCase of transporterCases) {
      const existingStart = timeToMinutes(existingCase.pickupTimeStart);
      const existingEnd = timeToMinutes(
        await this.getCaseEndTime(existingCase, transporterId, selectedDate, availableTransporters)
      );

      // 24時間をまたぐ場合の処理
      const normalizedNewEnd = newCaseEnd < newCaseStart ? newCaseEnd + 1440 : newCaseEnd;
      const normalizedExistingEnd = existingEnd < existingStart ? existingEnd + 1440 : existingEnd;

      // 重複判定
      if (newCaseStart < normalizedExistingEnd && normalizedNewEnd > existingStart) {
        return true; // 重複あり
      }
    }

    return false; // 重複なし
  }

  /**
   * FL-81: バッファ時間を考慮した時間重複をチェックする
   * @param newCase - 新規割り当て案件
   * @param transporterId - 陸送員ID
   * @param existingCases - 既存の案件リスト
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @param bufferSettings - バッファ時間設定
   * @returns 重複がある場合true
   */
  static async checkTimeOverlapWithBuffer(
    newCase: Case,
    transporterId: string,
    existingCases: Case[],
    selectedDate: string,
    availableTransporters: LandTransporter[],
    bufferSettings: BufferTimeSettings = DEFAULT_BUFFER_TIME_SETTINGS
  ): Promise<boolean> {
    const transporterCases = existingCases.filter(
      c => c.assignedLandTransporterId === transporterId
    );

    const newCaseStart = timeToMinutes(
      subtractBufferFromStartTime(newCase.pickupTimeStart, bufferSettings.betweenCasesBuffer)
    );
    const newCaseEnd = timeToMinutes(
      await this.getCaseEndTimeWithBuffer(
        newCase,
        transporterId,
        selectedDate,
        availableTransporters,
        bufferSettings
      )
    );

    for (const existingCase of transporterCases) {
      const existingStart = timeToMinutes(
        subtractBufferFromStartTime(existingCase.pickupTimeStart, bufferSettings.betweenCasesBuffer)
      );
      const existingEnd = timeToMinutes(
        await this.getCaseEndTimeWithBuffer(
          existingCase,
          transporterId,
          selectedDate,
          availableTransporters,
          bufferSettings
        )
      );

      // 24時間をまたぐ場合の処理
      const normalizedNewEnd = newCaseEnd < newCaseStart ? newCaseEnd + 1440 : newCaseEnd;
      const normalizedExistingEnd = existingEnd < existingStart ? existingEnd + 1440 : existingEnd;

      // バッファ時間を考慮した重複判定
      if (newCaseStart < normalizedExistingEnd && normalizedNewEnd > existingStart) {
        return true; // 重複あり
      }
    }

    return false; // 重複なし
  }

  /**
   * 勤務時間外かどうかをチェックする
   * @param newCase - 新規割り当て案件
   * @param transporterId - 陸送員ID
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns 勤務時間外の場合true
   */
  static async checkWorkingHoursViolation(
    newCase: Case,
    transporterId: string,
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<boolean> {
    try {
      // マイスケジュール取得
      const schedule = await this.getScheduleForDate(transporterId, selectedDate);

      // スケジュール未登録の場合は割り当て不可（厳格モード）
      if (!schedule) {
        return true;
      }

      // キャンセル済みスケジュールは割り当て不可
      if (schedule.status === 'cancelled') {
        return true;
      }

      const caseStart = newCase.pickupTimeStart;
      const caseEnd = await this.getCaseEndTime(
        newCase,
        transporterId,
        selectedDate,
        availableTransporters
      );

      // 案件時間がスケジュール時間外かチェック
      if (caseStart < schedule.timeStart || caseEnd > schedule.timeEnd) {
        return true; // 勤務時間外
      }

      return false; // 勤務時間内
    } catch (error) {
      console.error(`Failed to check working hours violation: ${error}`);
      return true; // エラー時は割り当て不可
    }
  }

  /**
   * 案件を陸送員に割り当て可能かを統合的にチェックする
   * @param newCase - 新規割り当て案件
   * @param transporterId - 陸送員ID
   * @param existingCases - 既存の案件リスト
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns バリデーション結果
   */
  static async validateCaseAssignment(
    newCase: Case,
    transporterId: string,
    existingCases: Case[],
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<ValidationResult> {
    // 勤務時間外チェック
    if (
      await this.checkWorkingHoursViolation(
        newCase,
        transporterId,
        selectedDate,
        availableTransporters
      )
    ) {
      return { canAssign: false, reason: '勤務時間外' };
    }

    // 時間重複チェック
    if (
      await this.checkTimeOverlap(
        newCase,
        transporterId,
        existingCases,
        selectedDate,
        availableTransporters
      )
    ) {
      return { canAssign: false, reason: '時間重複' };
    }

    return { canAssign: true };
  }

  /**
   * 複数の案件を一括でバリデーションする
   * @param cases - バリデーション対象の案件リスト
   * @param transporterId - 陸送員ID
   * @param existingCases - 既存の案件リスト
   * @param selectedDate - 選択日付
   * @param selectedDayOfWeek - 選択曜日（後方互換性のため）
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns 各案件のバリデーション結果のマップ
   */
  static async validateMultipleCaseAssignments(
    cases: Case[],
    transporterId: string,
    existingCases: Case[],
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<Map<string, ValidationResult>> {
    const results = new Map<string, ValidationResult>();

    for (const case_ of cases) {
      const result = await this.validateCaseAssignment(
        case_,
        transporterId,
        existingCases,
        selectedDate,
        availableTransporters
      );
      results.set(case_.id, result);
    }

    return results;
  }

  /**
   * 陸送員の当日の稼働時間を計算する
   * @param transporterId - 陸送員ID
   * @param assignedCases - 割り当てられた案件リスト
   * @param selectedDate - 選択日付
   * @param availableTransporters - 利用可能な陸送員リスト
   * @returns 稼働時間（分）
   */
  static async calculateWorkingMinutes(
    transporterId: string,
    assignedCases: Case[],
    selectedDate: string,
    availableTransporters: LandTransporter[]
  ): Promise<number> {
    let totalMinutes = 0;

    for (const case_ of assignedCases) {
      const startMinutes = timeToMinutes(case_.pickupTimeStart);
      const endMinutes = timeToMinutes(
        await this.getCaseEndTime(case_, transporterId, selectedDate, availableTransporters)
      );
      totalMinutes += endMinutes - startMinutes;
    }

    return totalMinutes;
  }

  // =========================================================================
  // Phase 5 Refactor: 後方互換性ラッパーメソッド
  // =========================================================================

  /**
   * 【後方互換性】陸送員の勤務時間を取得（workingDays互換）
   *
   * このメソッドは、従来の LandTransporter.workingDays フィールドからの移行期に使用
   * する互換性ラッパーです。
   *
   * 段階的移行戦略:
   * - Phase 5（現在）: 後方互換性維持、新DB参照メソッドと並行稼働
   * - Phase 6: DB参照に完全移行
   * - Phase 7: workingDays フィールド削除
   *
   * @param transporter LandTransporter オブジェクト
   * @param dayOfWeek 曜日（'monday' | 'tuesday' | ... | 'sunday'）
   * @returns 勤務時間（{startTime, endTime}）、存在しない場合は null
   *
   * @example
   * // 従来の書き方（非推奨・Phase 5では併用）
   * const slot = transporter.workingDays[dayOfWeek];
   *
   * // Phase 5推奨: ラッパーメソッド使用
   * const slot = ScheduleValidationService.getWorkingHours(transporter, dayOfWeek);
   *
   * // Phase 6+推奨: DB直接参照（将来的に移行予定）
   * const slot = await driverScheduleService.getDefaultScheduleForDayOfWeek(
   *   transporter.id,
   *   dayOfWeek
   * );
   */
  /**
   * 後方互換性のための勤務時間取得（非推奨）
   *
   * ⚠️ FL-259実装完了後は使用禁止
   * 代わりにgetScheduleForDate()を使用してください
   *
   * @deprecated Phase 6以降は削除予定
   */
  static getWorkingHours(
    transporter: any,
    dayOfWeek: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
  ): { startTime: string; endTime: string } | null {
    // workingDaysから取得（後方互換性のみ）
    if (transporter?.workingDays?.[dayOfWeek]) {
      return transporter.workingDays[dayOfWeek];
    }
    return null;
  }

  // =========================================================================
  // Phase "マイスケジュール統一化": driver_schedules 参照メソッド
  // =========================================================================

  /**
   * 特定日のdriver_schedulesを取得
   *
   * マイスケジュール（driver_schedules）から特定日のスケジュール情報を取得します。
   * workingDaysからの移行用メソッドです。
   *
   * @param transporterId - 陸送員ID
   * @param date - スケジュール日付（YYYY-MM-DD形式）
   * @returns マイスケジュール情報（status='cancelled'以外）、存在しない場合はnull
   * @throws エラーが発生した場合
   *
   * @example
   * const schedule = await ScheduleValidationService.getScheduleForDate(
   *   'transporter-001',
   *   '2025-11-10'
   * );
   * // schedule?.timeStart === '09:00'
   * // schedule?.timeEnd === '18:00'
   */
  static async getScheduleForDate(
    transporterId: string,
    date: string
  ): Promise<DriverSchedule | null> {
    try {
      const schedule = await driverScheduleService.getScheduleForDate(transporterId, date);
      return schedule;
    } catch (error) {
      console.error(`Failed to get schedule for date: ${error}`);
      throw error;
    }
  }

  /**
   * 案件時刻がdriver_schedulesの範囲内に収まるか検証（厳格モード）
   *
   * マイスケジュール（driver_schedules）に登録されている時間範囲内に
   * 案件が収まっているかを厳格にチェックします。
   *
   * @param transporterId - 陸送員ID
   * @param caseStartTime - 案件開始時刻（HH:mm形式）
   * @param caseEndTime - 案件終了時刻（HH:mm形式）
   * @param date - スケジュール日付（YYYY-MM-DD形式）
   * @returns { valid: true } or { valid: false, error: エラー理由 }
   *
   * @example
   * const result = await ScheduleValidationService.validateAssignmentAgainstSchedule(
   *   'transporter-001',
   *   '09:00',
   *   '18:00',
   *   '2025-11-10'
   * );
   * if (!result.valid) {
   *   console.log(result.error); // 'マイスケジュール未登録の日には割り当てできません'
   * }
   */
  static async validateAssignmentAgainstSchedule(
    transporterId: string,
    caseStartTime: string,
    caseEndTime: string,
    date: string
  ): Promise<{ valid: boolean; error?: string }> {
    try {
      // マイスケジュール取得
      const schedule = await this.getScheduleForDate(transporterId, date);

      // スケジュール未登録
      if (!schedule) {
        return {
          valid: false,
          error: 'マイスケジュール未登録の日には割り当てできません',
        };
      }

      // ステータス確認（status='cancelled'は除外）
      if (schedule.status === 'cancelled') {
        return {
          valid: false,
          error: 'キャンセルされたスケジュールです',
        };
      }

      // 時刻範囲チェック
      const scheduleStart = schedule.timeStart;
      const scheduleEnd = schedule.timeEnd;

      if (caseStartTime < scheduleStart || caseEndTime > scheduleEnd) {
        return {
          valid: false,
          error: `スケジュール外の時刻には割り当てできません (スケジュール: ${scheduleStart}-${scheduleEnd})`,
        };
      }

      return { valid: true };
    } catch (error) {
      console.error(`Failed to validate assignment against schedule: ${error}`);
      return {
        valid: false,
        error: 'スケジュール検証中にエラーが発生しました',
      };
    }
  }

  /**
   * 特定期間のdriver_schedulesを取得（パフォーマンス最適化）
   *
   * 開始日から終了日までの期間に含まれるスケジュール情報を一括取得します。
   * レポート生成やスケジュール画面の範囲表示用です。
   *
   * @param transporterId - 陸送員ID
   * @param startDate - 開始日付（YYYY-MM-DD形式）
   * @param endDate - 終了日付（YYYY-MM-DD形式）
   * @returns マイスケジュール情報の配列
   * @throws エラーが発生した場合
   *
   * @example
   * const schedules = await ScheduleValidationService.getSchedulesForDateRange(
   *   'transporter-001',
   *   '2025-11-01',
   *   '2025-11-30'
   * );
   * console.log(schedules.length); // その月のスケジュール件数
   */
  static async getSchedulesForDateRange(
    transporterId: string,
    startDate: string,
    endDate: string
  ): Promise<DriverSchedule[]> {
    try {
      // driverScheduleServiceが範囲取得メソッドを提供する場合
      // 現在はgetDriverSchedulesByTransporter で全件取得して フィルタリング
      const allSchedules =
        await driverScheduleService.getDriverSchedulesByTransporter(transporterId);

      const filtered = allSchedules.filter(
        schedule =>
          schedule.scheduleDate >= startDate &&
          schedule.scheduleDate <= endDate &&
          schedule.status !== 'cancelled'
      );

      return filtered;
    } catch (error) {
      console.error(`Failed to get schedules for date range: ${error}`);
      throw error;
    }
  }

  /**
   * スケジュール存在チェック（高速化用）
   *
   * 特定日にマイスケジュール登録がある（status!='cancelled'）かを簡単にチェック。
   *
   * @param transporterId - 陸送員ID
   * @param date - スケジュール日付（YYYY-MM-DD形式）
   * @returns スケジュール登録有無（true: あり、false: なし）
   *
   * @example
   * const hasSchedule = await ScheduleValidationService.hasScheduleForDate(
   *   'transporter-001',
   *   '2025-11-10'
   * );
   * if (!hasSchedule) {
   *   console.log('スケジュール未登録');
   * }
   */
  static async hasScheduleForDate(transporterId: string, date: string): Promise<boolean> {
    try {
      const schedule = await this.getScheduleForDate(transporterId, date);
      return schedule !== null && schedule.status !== 'cancelled';
    } catch (error) {
      console.error(`Failed to check schedule existence: ${error}`);
      return false;
    }
  }
}
